import React from "react";

import { useDispatch, useSelector } from "types/store";

import { VIEW_TYPE, getIsTile, getType, updateSearch, updateType } from "containers/View/reducer";
import { getIsSampleAccount } from "containers/Login/reducer";
import { getIsModelsLegacy } from "containers/Run/reducer";
import { getConsulting, getIsMarketplaceDemo } from "containers/MarketplaceDemo/reducer";

import RunViewContainer from "containers/View/Run";
import OptimizerViewContainer from "containers/View/Optimizer";
import BlueprintViewContainer from "containers/View/Blueprint";
import ChatBlueprintViewContainer from "containers/View/ChatBlueprint";
import DeploymentViewContainer from "containers/View/Deployment";
import ReportViewContainer from "containers/View/Report";
import ExplorationViewContainer from "containers/View/Exploration";
import ChatsViewContainer from "containers/View/Chats";
import TasksViewContainer from "containers/View/Tasks";
import OverviewViewContainer from "containers/View/Overview";
import NewChatContainer from "containers/NewChat/NewChat";
import ArtifactViewContainer from "containers/LanguageModelArtifact/ArtifactView";
import ChatAnalyticsContainer from "containers/ChatAnalytics/ChatAnalytics";
import AgentViewContainer from "components/AibleAgent/AgentView";
import TemplatesView from "components/View/TemplatesView";
import SparkTemplatesView from "@/components/View/Spark/SparkTemplateView";

import ChatAppViewContainer from "containers/ChatApp/ChatAppView";
import TestChatAppReportView from "components/ChatApp/TestChatAppReportView";

import SenseViewContainer from "containers/View/Sense";

// MarketplaceDemo
import ConsultingProjectsContainer from "containers/ConsultingProjects/ConsultingProjects";

import View from "components/View/View";
import TestChatAppMonitoring from "components/ChatApp/TestChatAppMonitoring";
import OrganizationAnalytics from "@/containers/OrganizationAnalytics/OrganizationAnalytics";
import { isEnabled } from "@/utils/featureFlags";
import NewChatSparkPartners from "@/components/NewChat/NewChatSparkPartners";

const ViewContaierContentItem = ({ type, isSampleAccount, guide }) => {
  switch (type) {
    case VIEW_TYPE.AGENTS:
      return <AgentViewContainer key={type} />;

    case VIEW_TYPE.ARTIFACTS:
      return <ArtifactViewContainer key={type} />;

    case VIEW_TYPE.CHAT_ANALYTICS:
      return <ChatAnalyticsContainer key={type} />;

    case VIEW_TYPE.NEW_CHAT:
      return isEnabled("spark-agents-templates-view") ? (
        <NewChatSparkPartners key={type} />
      ) : (
        <NewChatContainer key={type} />
      );

    case VIEW_TYPE.OVERVIEW:
      return <OverviewViewContainer key={type} />;

    case VIEW_TYPE.RUNS:
      return <RunViewContainer key={type} />;

    case VIEW_TYPE.DATASETS:
      return <SenseViewContainer key={type} hideFirstTile={isSampleAccount} />;

    case VIEW_TYPE.BLUEPRINTS:
      return <BlueprintViewContainer key={type} resetDataset guide={guide} />;

    case VIEW_TYPE.REPORTS:
      return <ReportViewContainer key={type} />;

    case VIEW_TYPE.DEPLOYMENTS:
    case VIEW_TYPE.CHAT_APPS:
      return <DeploymentViewContainer key={type} />;

    case VIEW_TYPE.APPS_AGENTS:
      return <ChatAppViewContainer />;

    case VIEW_TYPE.MONITORING:
      return <TestChatAppMonitoring />;

    case VIEW_TYPE.PROJECTS:
      return <ConsultingProjectsContainer key={type} />;

    case VIEW_TYPE.OPTIMIZERS:
      return <OptimizerViewContainer key={type} />;

    case VIEW_TYPE.EXPLORATIONS:
      return <ExplorationViewContainer key={type} />;

    case VIEW_TYPE.CHATS:
      return <ChatsViewContainer key={type} />;

    case VIEW_TYPE.CHAT_BLUEPRINTS:
      return <ChatBlueprintViewContainer key={type} resetDataset guide={guide} />;

    case VIEW_TYPE.AGENT_TEMPLATES:
      return isEnabled("spark-agents-templates-view") ? (
        <SparkTemplatesView key={type} />
      ) : (
        <TemplatesView key={type} />
      );

    case VIEW_TYPE.TASKS:
      return <TasksViewContainer key={type} />;

    case VIEW_TYPE.ORGANIZATION_ANALYTICS:
      return <OrganizationAnalytics key={type} />;

    default:
      return <div key={type} />;
  }
};

const ViewContainerContent = ({ type, typeOptions, guide }) => {
  const isSampleAccount = useSelector((state) => getIsSampleAccount(state));

  if (type == VIEW_TYPE.ALL) {
    return typeOptions.map((option) => this.renderType(option));
  }

  return <ViewContaierContentItem type={type} isSampleAccount={isSampleAccount} guide={guide} />;
};

const IS_MODELS_LEGACY = getIsModelsLegacy();
const IS_MARKETPLACE_DEMO = getIsMarketplaceDemo();

const ViewContainer = ({ guide, typeOptions }) => {
  const dispatch = useDispatch();

  const isTile = useSelector((state) => getIsTile(state));
  const type = useSelector((state) => getType(state));

  const handleSearch = (search) => dispatch(updateSearch(search));

  const defaultOptions = [
    VIEW_TYPE.OPTIMIZERS,
    VIEW_TYPE.BLUEPRINTS,
    VIEW_TYPE.DATASETS,
    VIEW_TYPE.DEPLOYMENTS,
    VIEW_TYPE.EXPLORATIONS
  ];

  const showConsulting = useSelector(
    (state) =>
      getConsulting(state).filter(
        (elem) => elem && elem.wizard && elem.wizard.marketplace && elem.wizard.marketplace.purchased
      ).length > 0
  );

  const projectsIsEnabled = showConsulting && IS_MARKETPLACE_DEMO;

  if (projectsIsEnabled) {
    defaultOptions.push(VIEW_TYPE.PROJECTS);
  }

  if (IS_MODELS_LEGACY) {
    defaultOptions.push(VIEW_TYPE.RUNS);
  }

  const finalTypeOptions = typeOptions ?? defaultOptions;

  return (
    <View
      type={type}
      typeOptions={finalTypeOptions}
      updateType={(type) => dispatch(updateType(type))}
      updateSearch={handleSearch}
      isTile={isTile}
      hideMenu
    >
      <ViewContainerContent guide={guide} type={type} typeOptions={finalTypeOptions} />
    </View>
  );
};

export default ViewContainer;
